import 'whatwg-fetch';
import uuidv4 from 'uuid/v4';

const DEFAULT_REQUEST_TIMEOUT = 10000;
const DEFAULT_ALLOWED_ORIGIN = /.+/;
const isEmbedded = window.parent && window.parent !== window;

/**
 * Request registry, tracks messages sent to the parent by ID.
 */
const requestRegistry = {};

function registerRequest(messageId, resolve, reject, allowedOriginPattern, timeout) {
    console.log("Registering request for tracking.", {timeout: timeout, messageId: messageId});
    requestRegistry[messageId] = {
        resolve: resolve,
        reject: reject,
        allowedOriginPattern: allowedOriginPattern,
        timeout: setTimeout(timeoutRequest.bind(null, messageId), timeout || DEFAULT_REQUEST_TIMEOUT)
    }
}

function handleResponse(origin, messageId, data) {
    console.log("Receiving message from frame.", {origin: origin, messageId: messageId})
    const request = requestRegistry[messageId];
    if (!request) {
        console.warn("No message found for messageId.", messageId)
        return;
    }
    clearTimeout(request.timeout);

    if(!request.allowedOriginPattern.test(origin)) {
        request.reject(`Origin does not match configured pattern: ${origin}`);
    } else {
        //rejecting a request in case of UNAUTHORIZED or Error response.
        if(data.$sdk_responseType != undefined && data.$sdk_responseType != "SUCCESS") {
            console.error("Error receiving from parent application.", {responseType: data.$sdk_responseType});
            request.reject(data);
        } else {
            console.info("Successful response for message.", {messageId: messageId});
            request.resolve(data);
        }
    }
}

function timeoutRequest(messageId) {

    //following the standard at gw.sdk.web.WebHandlerUtil.InvokeResponse
    const timeOutError = {
        'messageId': messageId,
        'result' : {'errorMessage' : 'request timed out'},
        '$sdk_responseType' : 'ERROR'
    };

    requestRegistry[messageId].reject(timeOutError);
    console.error(`Request ${messageId} to parent frame timed out: ${requestRegistry[messageId]}`);
    delete requestRegistry[messageId];
}

window.addEventListener('message', (event) => {
    handleResponse(event.origin, event.data.messageId, event.data.data)
}, false);


/**
 * Module entry point, exposed as a function that takes the configuration for the client that will be created.
 * @param {string} vendor - Name of the Vendor using the client.
 * @param {string} addon - Name of the Addon using the client.
 * @param {string} jwtToken - JWT token for vendor authentication
 * @param {RegExp} allowedOriginPattern - A regex that will be matched against the parent frame's origin. The
 * client will not be created if this pattern does not match.
 * @returns {{getClient: getClient}}
 */
export function createClient(vendor, addon, jwtToken, allowedOriginPattern) {
    console.log("Creating new client for addon", vendor, addon)
    //TODO: track additional creations of an identical client, use the same promise.
    allowedOriginPattern = allowedOriginPattern || DEFAULT_ALLOWED_ORIGIN;
    //Store the origin the browser gives us on the first response
    let applicationPath;
    //Our singleton client, initialized after the first response.
    let gwClient;
    //Keep track of requests for the client before it is initialized
    const clientReqs = [];

    //Setup the invoke function for the given vendor + addon
    const invoke = (command, params, timeout, refresh) => {
        const messageId = uuidv4();
        const dataToSend = {
            "cmd": command,
            "messageId": messageId,
            "vendor": vendor,
            "addon": addon,
            "jwtToken": jwtToken,
            "data": params,
            "origin": location.origin,
            "refresh": refresh
        };
        //not to post if application is not embedded in iframe
        if (isEmbedded) {
            return new Promise((resolve, reject) => {
                registerRequest(messageId, resolve, reject, allowedOriginPattern, timeout);
                console.log("Sending request to parent page.", {messageId: dataToSend.messageId, refresh: dataToSend.refresh});
                window.parent.postMessage(dataToSend, "*");
            });
        } else {
            return new Promise((resolve, reject) => {
                reject('No parent frame detected, unable to send message.');
            });
        }
    };

    invoke("identify")
        .then((data) => {
            console.info("Application identified, setting up GW Client...");
            applicationPath = data["applicationPath"];
            gwClient = {
                /**
                 * Retrieves the context information for the current InsuranceSuite page.
                 * @param {number} timeout - the amount of time to wait for a response
                 * @returns {*}
                 */
                getContext: (timeout) => {
                    console.log("Loading application context...");
                    return invoke("getContext", {}, timeout, true);
                },

                /**
                 * Invokes the given command and refreshes the UI in the InsuranceSuite application.
                 * Your frame may be destroyed as a result of this command.
                 * @param {string} command - the command to run
                 * @param {object} params - parameters for the command
                 * @param {number} timeout - the amount of time to wait for a response
                 * @return {Promise<*>}
                 */
                invokeWithRefresh: (command, params, timeout) => {
                    return invoke(command, params, timeout, true);
                },

                /**
                 * Invokes the given command, but does not refresh the UI in the InsuranceSuite application.
                 * Your frame cannot be destroyed as a result of this command, but any updates to data
                 * on the InsuranceSuite side will not be reflected in the User's browser.
                 *
                 * This should only be used to gather data, not to alter it.
                 *
                 * @param {string} command - the command to run
                 * @param {object} params - parameters for the command
                 * @param {number} timeout - the amount of time to wait for a response.
                 * @return {Promise<*>}
                 */
                invokeWithoutRefresh: (command, params, timeout) => {
                    return invoke(command, params, timeout, false);
                },

                /**
                 * Navigates the parent InsuranceSuite application to the given page. The context of the page
                 * will be the entity matching the given publicId, if applicable.
                 * @param {string} page - The page name to navigate to.
                 * @param {string} publicId - The public id of the context entity to load, if applicable for the
                 * given page.
                 * @param {number} timeout - The amount of time to wait for a response.
                 * @returns {*}
                 * @deprecated use completed(publicId, timeout) or canceled(timeout)
                 */
                navigate: (page, publicId, timeout) => {
                    console.warn("Deprecated navigate API in use, please use canceled() or complete()")
                    if (publicId) {
                        gwClient.completed(publicId, timeout)
                    } else {
                        gwClient.canceled(timeout)
                    }
                },

                /**
                 * Notify the parent InsuranceSuite application that the embedded page has completed its task.
                 * @param {string} publicId - The public id of the context entity to load, if applicable for the
                 * given page.
                 * @param {number} timeout - The amount of time to wait for a response.
                 * @returns {*|Promise<*>}
                 */
                completed: (publicId, timeout) => {
                    console.log("Sending completed request...");
                    return gwClient.invokeWithRefresh("completed", {
                        publicId: publicId
                    }, timeout);
                },

                /**
                 * Notify the parent InsuranceSuite application that the embedded page has canceled its task.
                 * @param {number} timeout - The amount of time to wait for a response.
                 * @returns {*|Promise<*>}
                 */
                canceled: (timeout) => {
                    console.log("Sending cancel request...");
                    return gwClient.invokeWithRefresh("canceled", {}, timeout);
                },

                /**
                 * Makes a request to the given path in embedding InsuranceSuite application.
                 *
                 * @param {string} httpMethod - the Http Method to use for the request
                 * @param {string} path - the path to request, relative to the Addon's configured REST api
                 * @param {*} body - the request body
                 * @returns {Promise<Response>}
                 */
                httpRequest: (httpMethod, path, body) => {
                    const url = applicationPath + '/devconnect/' + vendor + '/' + addon + path;
                    const myHeaders = new Headers();
                    myHeaders.append("Content-Type", 'application/json');
                    if(jwtToken) {
                        myHeaders.append("Authorization", 'Bearer ' + jwtToken);
                    }
                    return fetch(url, {
                        body: body,
                        method: httpMethod.toUpperCase(),
                        headers: myHeaders
                    });
                }
            };
            //Resolve any early requests
            clientReqs.forEach((value) => value.resolve(gwClient));
        })
        .catch((err) => {
            console.error("Unable to initialize client: " + err);
            //Reject any early requests
            clientReqs.forEach((value) => value.reject(gwClient));
        });

    return {
        /**
         * Returns a Promise that resolves to the initialized client.
         * @returns {Promise}
         */
        getClient: () => {
            return new Promise((resolve, reject) => {
                if (gwClient) {
                    console.log("Client already initialized, returning cached client.")
                    resolve(gwClient);
                } else {
                    console.log("Attempt to get client before initialization is complete, client will resolve later.")
                    clientReqs.push({
                        resolve: resolve,
                        reject: reject
                    });
                }
            });
        }
    };
};
